// Authentication hook
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { queryKeys } from '@/lib/query-client'
import type { User } from '@/types/entities'
import type { LoginRequest } from '@/types/api.types'
import type { Database } from '@/types/database.types'

/**
 * Get current authenticated user with profile
 */
export function useCurrentUser() {
  return useQuery({
    queryKey: queryKeys.auth.user(),
    queryFn: async (): Promise<User | null> => {
      const {
        data: { user: authUser },
        error: authError,
      } = await supabase.auth.getUser()

      if (authError || !authUser) {
        // No authenticated user
        return null
      }

      // Fetch user profile from public.users table (admin users only)
      const { data: profile, error: profileError } = await supabase
        .from('users')
        .select('*')
        .eq('id', authUser.id)
        .maybeSingle()

      // If profile doesn't exist, this might be a staff or assistant user
      // Don't sign out - just return null and let the staff/assistant hooks handle it
      if (profileError) {
        console.error('Profile fetch error:', profileError)
        return null
      }

      if (!profile) {
        // User might be staff or assistant - don't sign out, just return null
        return null
      }

      // Map database row to entity
      return {
        id: profile.id,
        name: profile.name,
        role: profile.role,
        restaurantId: profile.restaurant_id || null,
        languagePreference: profile.language_preference || 'fr',
        isActive: profile.is_active ?? true,
        isFirstLogin: profile.is_first_login ?? false,
        createdBy: profile.created_at || '',
        deletedAt: profile.deleted_at || null,
        createdAt: profile.created_at || '',
        updatedAt: profile.updated_at || '',
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false,
  })
}

/**
 * Get current session
 */
export function useSession() {
  return useQuery({
    queryKey: queryKeys.auth.session(),
    queryFn: async () => {
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession()

      if (error) {
        throw error
      }

      return session
    },
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Login mutation
 */
export function useLogin() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (credentials: LoginRequest) => {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password,
      })

      if (error) {
        throw error
      }

      return data
    },
    onSuccess: () => {
      // Invalidate auth queries to refetch user data
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.user() })
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.session() })
      // Also invalidate restaurants to refetch with new auth context
      queryClient.invalidateQueries({ queryKey: queryKeys.restaurants.all() })
    },
  })
}

/**
 * Logout mutation
 */
export function useLogout() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const { error } = await supabase.auth.signOut()

      if (error) {
        throw error
      }
    },
    onSuccess: () => {
      // Clear all queries on logout
      queryClient.clear()
    },
  })
}

/**
 * Check if user has specific role
 */
export function useHasRole(role: Database['public']['Enums']['user_role']) {
  const { data: user } = useCurrentUser()
  return user?.role === role
}

/**
 * Check if user is admin
 */
export function useIsAdmin() {
  return useHasRole('admin')
}

/**
 * Check if user is authenticated
 */
export function useIsAuthenticated() {
  const { data: user, isLoading } = useCurrentUser()
  return { isAuthenticated: !!user, isLoading }
}
